import { PlusCircleOutlined } from '@ant-design/icons';
import { Button, Col, Divider, Row, Space, Typography } from 'antd';
import { useFormikContext } from 'formik';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { type FormValues } from '.';
import SocialMediaInputFieldArray from '../../../../components/fields/SocialMediaInputFieldArray';
import TranslatedTextAreaField from '../../../../components/fields/TranslatedTextAreaField';
import CollapsibleWrapper, { Panel } from '../../../../components/wrappers/CollapsibleWrapper';
import FormFields from '../../../../themes/admin/Fields/FormFields';
import useMarkDownInfoTooltip from '../../../../utilities/useMarkDownInfoTooltip';
import { StyledExtraConfigurationTableHeader } from '../../../shared/AdminSharedUI';

const colSpan = { lg: 8, md: 12, xs: 24 };
const { Title } = Typography;

type DealerContactProps = {
    disabled?: boolean;
};

const DealerContact = ({ disabled }: DealerContactProps) => {
    const { t } = useTranslation('dealerForm');
    const {
        values: { socialMedia },
        setFieldValue,
    } = useFormikContext<FormValues>();

    const markdownInfoTooltip = useMarkDownInfoTooltip();
    const addSocialMedia = useCallback(() => {
        setFieldValue('socialMedia', [...socialMedia, { title: { defaultValue: '', overrides: [] }, content: '' }]);
    }, [socialMedia, setFieldValue]);

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <CollapsibleWrapper defaultActiveKey={t('dealerForm:contactTitle')}>
                <Panel key={t('dealerForm:contactTitle')} header={t('dealerForm:contactTitle')}>
                    <Row gutter={14}>
                        <Col {...colSpan}>
                            <FormFields.PhoneAndPrefixField
                                {...t('dealerForm:fields.dealerInput.contact.telephone', { returnObjects: true })}
                                disabled={disabled}
                                name="dealerInput.contact.telephone"
                                allowSpecialCharacters
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.InputField
                                {...t('dealerForm:fields.dealerInput.contact.email', { returnObjects: true })}
                                disabled={disabled}
                                name="dealerInput.contact.email"
                            />
                        </Col>
                        <Col {...colSpan}>
                            <TranslatedTextAreaField
                                {...t('dealerForm:fields.dealerInput.contact.address', { returnObjects: true })}
                                disabled={disabled}
                                name="dealerInput.contact.address"
                            />
                        </Col>
                        <Col {...colSpan}>
                            <TranslatedTextAreaField
                                {...t('dealerForm:fields.dealerInput.contact.additionalInfo', { returnObjects: true })}
                                disabled={disabled}
                                name="dealerInput.contact.additionalInfo"
                                tooltip={markdownInfoTooltip}
                            />
                        </Col>
                    </Row>

                    <Divider />
                    <StyledExtraConfigurationTableHeader>
                        <Title level={5} style={{ fontSize: '14px' }}>
                            {t('dealerForm:socialMediaIcons')}
                        </Title>
                        {!disabled && (
                            <Button icon={<PlusCircleOutlined />} onClick={addSocialMedia} type="link">
                                {t('dealerForm:actions.addSocialMediaIcons')}
                            </Button>
                        )}
                    </StyledExtraConfigurationTableHeader>

                    <SocialMediaInputFieldArray disabled={disabled} name="socialMedia" />
                </Panel>
            </CollapsibleWrapper>
        </Space>
    );
};

export default DealerContact;
